<template>
  <div class=" bg-gray-900 text-gray-100 rounded-lg backdrop-blur" dir="rtl">
    <header class="top-0 z-30">
      <div class=" px-3 sm:px-1 lg:px-6 py-1 flex items-center justify-between">
        <h1 class="text-md mt-1 font-extrabold text-gray-800">
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500">
            {{ id ? 'ویرایش محصول' : 'ایجاد محصول جدید' }}
          </span>
        </h1>
      </div>
    </header>

    <div v-if="loading && id" class="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-14 flex justify-center">
      <svg class="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
           viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.375 0 0 5.375 0 12h4z"/>
      </svg>
    </div>

    <!-- Content -->
    <div v-else class="mx-auto px-3 sm:px-4 lg:px-6 py-4">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">

        <main class="lg:col-span-9 space-y-3">

          <section class="bg-white dark:bg-slate-900/70 rounded-xl border border-gray-200 dark:border-white/10 p-4">
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-1">

                <Label required>نام محصول</Label>
                <input v-model="form.name" type="text" :disabled="user.user_type == 'prtnr'"
                       class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80"
                       :class="{'ring-1 ring-rose-300': errors.name}">
                <FieldError v-if="errors.name" :msg="errors.name"/>
              </div>
              <div class="space-y-1">
                <Label required>کد SKU</Label>
                <div class="flex gap-2">
                  <input v-model="form.sixteen_digit_code" type="text" :disabled="user.user_type == 'prtnr'"
                         class="flex-1 px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[12px] text-center bg-blue-50/80"
                         :class="{'ring-1 ring-rose-300': errors.sixteen_digit_code}">
                  <button
                      type="button"
                      class="px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      @click="generateSku"
                      title="تولید SKU تصادفی"
                  >
                    🎲
                  </button>
                </div>
                <FieldError v-if="errors.sixteen_digit_code" :msg="errors.sixteen_digit_code"/>
              </div>

            </div>
          </section>

          <section class="bg-white rounded-lg overflow-hidden">
            <button type="button"
                    class="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-gray-50 border-b"
                    @click="togglePanel('content')">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586l6 6V19a2 2 0 01-2 2z"/>
                </svg>
                <h2 class="text-sm font-semibold text-gray-800">محتوای محصول</h2>
              </div>
              <Chevron :open="isOpen('content')"/>
            </button>

            <div v-show="isOpen('content')" class="p-2 flex">
              <nav>
                <div class="rounded-lg p-1">
                  <ul class="flex-col gap-1" role="tablist" :aria-orientation="'vertical'">
                    <li v-for="t in contentTabs" :key="t.key" class="justify-center items-center w-full">
                      <button
                          style="margin: auto"
                          class="w-full block text-center items-center px-4 py-3 my-1 rounded-lg transition-all duration-300
                   flex items-center justify-between group relative overflow-hidden"
                          :class="currentContentTab === t.key
                            ? 'bg-blue-600 text-white font-semibold border-r-4 border-blue-400'
                            : 'bg-gray-200 hover:bg-gray-700 text-gray-600 hover:text-white border-r-4 border-transparent hover:border-gray-600'"
                          role="tab"
                          :aria-selected="currentContentTab === t.key"
                          @click="setContentTab(t.key)"
                      >
                        <div
                            v-if="currentContentTab === t.key"
                            class="absolute right-0 top-0 h-full w-1 bg-gradient-to-b from-blue-400 to-blue-600 rounded-l"
                        ></div>

                        <div class="flex items-center gap-3 flex-1 min-w-0">
                          <span class="truncate text-sm font-medium">{{ t.label }}</span>
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              </nav>

              <div class=" flex-1">
                <!-- توضیحات -->
                <section v-show="currentContentTab === 'explanation'">
                  <Label>توضیحات</Label>
                  <quill-editor :disabled="user.user_type == 'prtnr'" v-model="form.explanation"
                                :options="editorOptions" class="q-wrapper"/>
                  <FieldError v-if="errors.explanation" :msg="errors.explanation"/>
                </section>

                <!-- توضیحات کوتاه -->
                <section v-show="currentContentTab === 'summary_explanation'">
                  <Label>توضیحات کوتاه</Label>
                  <quill-editor :disabled="user.user_type == 'prtnr'" v-model="form.summary_explanation"
                                :options="editorOptions" class="q-wrapper"/>
                  <FieldError v-if="errors.summary_explanation" :msg="errors.summary_explanation"/>
                </section>

                <!-- نحوه مصرف -->
                <section v-show="currentContentTab === 'how_to_use'">
                  <Label>نحوه مصرف</Label>
                  <quill-editor :disabled="user.user_type == 'prtnr'" v-model="form.how_to_use" :options="editorOptions"
                                class="q-wrapper"/>
                  <FieldError v-if="errors.how_to_use" :msg="errors.how_to_use"/>
                </section>

                <!-- سوالات متداول -->
                <section v-show="currentContentTab === 'faq'">
                  <Label>سوالات متداول</Label>
                  <quill-editor :disabled="user.user_type == 'prtnr'" v-model="form.faq" :options="editorOptions"
                                class="q-wrapper"/>
                  <FieldError v-if="errors.faq" :msg="errors.faq"/>
                </section>

                <!-- بلاگ -->
                <section v-show="currentContentTab === 'blog'">
                  <Label>بلاگ</Label>
                  <quill-editor :disabled="user.user_type == 'prtnr'" v-model="form.blog" :options="editorOptions"
                                class="q-wrapper"/>
                  <FieldError v-if="errors.blog" :msg="errors.blog"/>
                </section>
              </div>
            </div>
          </section>
          <section class="bg-white rounded-xl border border-gray-200 overflow-hidden"
                   v-if="user.user_type == 'ma' || user.user_type == 'prtnr'">
            <button type="button"
                    class="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-gray-50 border-b"
                    @click="togglePanel('pricing')">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586l6 6V19a2 2 0 01-2 2z"/>
                </svg>
                <h2 class="text-sm font-semibold text-gray-800">قیمت گذاری</h2>
              </div>
              <Chevron :open="isOpen('pricing')"/>
            </button>

            <div v-show="isOpen('pricing')" class="p-4">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label for="currency" class="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    نوع ارز
                  </label>
                  <select
                      v-model="form.currency"
                      id="currency"
                      class="select-input w-full"
                      :class="{'border-red-300': errors.currency}"
                  >
                    <option v-for="currency in currencies" :key="currency.id" :value="currency.id">
                      {{ currency.name }}
                    </option>
                  </select>
                  <p v-if="errors.currency" class="mt-1 text-sm text-red-600">{{ errors.currency }}</p>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-6 gap-6 mt-3">
                <div class="md:col-span-2" v-if="form.currency == 19">
                  <cleave-money
                      :required="true"
                      label="قیمت مصرف کننده"
                      v-model="form.regular_price"
                      currency-unit="تومان"
                  />
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-6 gap-6 mt-3" v-if="form.product_type != 'variable' ">
                <div class="md:col-span-2">
                  <cleave-money
                      :rate="current_currency.exchange_rate_to_toman"
                      :required="true"
                      label="قیمت پایه"
                      v-model="form.base_price"
                      :currency-unit="current_currency.name"
                      :label-down-value="base_price_with_rate"
                      label-down="برابر با "
                  />
                </div>
                <div class="md:col-span-2">
                  <cleave-money
                      :required="true"
                      label="هزینه ارسال"
                      v-model="form.shipping_cost"
                      :currency-unit="current_currency.name"
                      :label-down-value="shipping_cost_with_rate"
                      label-down="برابر با "
                  />
                </div>
                <div class="md:col-span-2">
                  <cleave-money
                      disabled="true"
                      label="مبلغ پایه با احتساب هزینه ارسال"
                      v-model="form.shipping_cost + form.base_price"
                      :currency-unit="current_currency.name"
                      :label-down-value="shipping_cost_with_rate + base_price_with_rate"
                      label-down="برابر با "
                  />
                </div>
                <div class="md:col-span-1" v-if="form.legend_pricing == 'true'">
                  <label for="profit_type" class="block text-[10px] font-medium text-gray-700 mb-1 flex items-center">
                    <span class="text-red-500 ml-1">*</span>
                    نوع حاشیه سود
                  </label>
                  <div>
                    <select
                        id="profit_type"
                        v-model="form.profit_type"
                        class="select-input w-full"
                    >
                      <option value="percent">درصد</option>
                      <option value="amount">مبلغ</option>
                    </select>

                  </div>
                </div>
                <div class="md:col-span-1" v-if="form.legend_pricing == 'true'">
                  <label for="profit_margin" class="block text-[10px] font-medium text-gray-700 mb-1 flex items-center">
                    <span class="text-red-500 ml-1">*</span>
                    حاشیه سود
                    <span class="text-[7px] mr-1 ml-1">برابر</span>
                    {{ profit_sum | toMoney }}
                    <span class="text-[7px] mr-1">تومان</span>
                  </label>
                  <div class="relative rounded-md shadow-sm">
                    <input
                        v-model.number="form.profit_margin"
                        type="number"
                        id="profit_margin"
                        min="0"
                        class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[13px] text-center bg-blue-50/80"
                        :class="{'border-red-300': errors.profit_margin}"
                    >
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-[8px] text-gray-500" v-if="form.profit_type == 'amount'">تومان</span>
                      <span class="text-[8px] text-gray-500" v-else>درصد</span>
                    </div>
                  </div>
                  <label for="profit_margin" class="block text-[8px] mb-1 pt-2 flex items-center">
                    مبلغ با حاشیه سود
                    ( {{ (profit_sum + base_price_with_rate + shipping_cost_with_rate) | toMoney }} تومان)
                  </label>

                  <p v-if="errors.profit_margin" class="mt-1 text-sm text-red-600">{{ errors.profit_margin }}</p>
                </div>
                <div class="md:col-span-1" v-if="form.legend_pricing == 'true'">
                  <label for="profit_type" class="block text-[10px] font-medium text-gray-700 mb-1 flex items-center">
                    <span class="text-red-500 ml-1">*</span>
                    نوع تخفیف
                  </label>
                  <div>
                    <select
                        id="discount_type"
                        v-model="form.discount_type"
                        class="select-input w-full"
                    >
                      <option value="percent">درصد</option>
                      <option value="amount">مبلغ</option>
                    </select>

                  </div>
                </div>
                <div class="md:col-span-1" v-if="form.legend_pricing == 'true'">
                  <label for="discount_amount"
                         class="block text-[10px] font-medium text-gray-700 mb-1 flex items-center">
                    <span class="text-red-500 ml-1">*</span>
                    مقدار تخفیف
                    {{ discount_sum | toMoney }}
                    <span class="text-[7px] mr-1">تومان</span>

                  </label>
                  <div class="relative rounded-md shadow-sm">
                    <input
                        v-model.number="form.discount_margin"
                        type="number"
                        id="discount_amount"
                        min="0"
                        class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[13px] text-center bg-blue-50/80"
                        :class="{'border-red-300': errors.discount_margin}"
                    >
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 text-sm text-[8px] text-gray-500 text-sm"
                            v-if="form.discount_type == 'amount'">تومان</span>
                      <span class="text-gray-500 text-sm text-[8px] text-gray-500 text-sm" v-else>درصد</span>
                    </div>
                  </div>
                  <p v-if="errors.discount_margin" class="mt-1 text-sm text-red-600">{{ errors.discount_amount }}</p>
                </div>
                <div class="md:col-span-2" v-if="form.legend_pricing == 'true'">
                  <cleave-money
                      :required="true"
                      label="قیمت نهایی فروش"
                      v-model="final_amount"
                      currency-unit="تومان"
                      :disabled="true"
                  ></cleave-money>
                </div>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-xl border border-gray-200 overflow-hidden" v-if="form.product_type != 'variable' &&
          (user.user_type == 'ma' || user.user_type == 'prtnr')">
            <button type="button" class="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-gray-50 border-b"
                    @click="togglePanel('inventory')">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M20 7l-8-4-8 4v10l8 4 8-4V7z"/>
                </svg>
                <h2 class="text-sm font-semibold text-gray-800">موجودی</h2>
              </div>
              <div class=" ml-4">
                  <span class="inline-flex gap-1 px-3 py-1.5 rounded-lg text-xs font-medium
                               bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-sm">
                    <svg class="w-3.5 h-3.5 text-white opacity-80" fill="none" stroke="currentColor" stroke-width="2"
                         viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4v10l8 4 8-4V7z"/>
                    </svg>
                    <span>موجودی کل:</span>
                    <b class="font-extrabold text-white">{{ total_inventory | toMoney }}</b>
                  </span>
              </div>

              <Chevron :open="isOpen('inventory')"/>
            </button>

            <div v-show="isOpen('inventory')" class="p-4">
              <div class="flex items-center justify-between mb-3">
                <div class="col-span-1" v-show="false">
                  <cleave-money
                      :disabled="user.user_type == 'prtnr'"
                      :required="false"
                      label="حداقل موجودی"
                      v-model="form.packing_inventory.minimum_inventory"
                      currency-unit="عدد"

                  ></cleave-money>
                </div>
                <button
                    :disabled="user.user_type == 'prtnr'"
                    v-if="!has_new_inventory_row"
                    @click="addInventoryRow"
                    type="button"
                    class="px-3 py-1.5 rounded-md bg-blue-600 text-xs hover:bg-blue-700"
                >
                  <span class="text-gray-50">افزودن موجودی انبار جدید</span>
                </button>
              </div>

              <!-- جدول -->
              <div class="rounded-lg border border-gray-200 overflow-hidden">
                <table class="min-w-full text-xs">
                  <thead class="bg-gray-50 text-gray-600">
                  <tr>
                    <th class="px-3 py-2 text-right">انبار</th>
                    <th class="px-3 py-2 text-right">موجودی</th>
                    <th class="px-3 py-2 text-right w-28"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-if="inventoriesLoading">
                    <td colspan="3" class="px-3 py-6 text-center text-gray-500">در حال بارگذاری...</td>
                  </tr>

                  <tr
                      v-for="(row, idx) in inventories"
                      :key="row._key"
                      class="border-t"
                  >
                    <td class="px-3 py-2">
                      <div v-if="row._isNew" class="flex items-center gap-2">
                        <store-select
                            :disabled="user.user_type == 'prtnr'"
                            v-model="new_store_for_inventory"
                            :not_in="inventory_store_ids"
                        />
                      </div>
                      <div v-else class="text-gray-800">
                        {{ row.store_name || ('انبار #' + row.store) }}
                      </div>
                    </td>

                    <td class="px-3 py-2">
                      <div class="flex items-center gap-2">
                        <input
                            :disabled="user.user_type == 'prtnr'"
                            type="number"
                            min="0"
                            class="w-32 px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[12px] text-center bg-blue-50/80"
                            v-model.number="row._inventoryDraft"
                        />
                      </div>
                    </td>

                    <td class="px-3 py-2">
                      <div class="flex items-center gap-2">
                        <button
                            v-if="isRowDirty(row)"
                            class="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                            :disabled="row._saving || !canSaveRow(row) || user.user_type == 'prtnr'"
                            @click="saveInventoryRow(row)"
                            title="ذخیره‌ی این ردیف"
                        >
                          ذخیره
                        </button>
                        <button
                            class='px-2 py-1 rounded bg-green-600 text-white  text-xs'
                            v-else
                            disabled
                        >
                          ✔
                        </button>

                        <button
                            :disabled="user.user_type == 'prtnr'"
                            v-if="row._isNew"
                            class="px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                            @click="removeNewRow(row)"
                            title="لغو ردیف جدید"
                        >
                          لغو
                        </button>
                      </div>
                    </td>
                  </tr>

                  <tr v-if="!inventoriesLoading && !inventories.length">
                    <td colspan="3" class="px-3 py-6 text-center text-gray-500">برای این محصول موجودی انباری ثبت نشده
                      است
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          <section class="bg-white rounded-xl border border-gray-200 p-4 mb-3">
            <h2 class="text-sm font-semibold text-gray-800 mb-4">تنظیمات رنگ تست مجازی رژلب</h2>
            <div class="mb-3">
                <label class="block">کد رنگ تست مجازی رژلب (اختیاری)</label>
                <input v-model="form.try_on_color_hex" dir="ltr" maxlength="7" placeholder="#BE536A" class="border rounded px-3 py-2" />
                <span :style="{backgroundColor: /^#[0-9a-f]{6}$/i.test(form.try_on_color_hex) ? form.try_on_color_hex : 'transparent', display: 'inline-block', width: '28px', height: '28px', border: '1px solid #ccc', verticalAlign: 'middle'}"></span>
                <p v-if="form.try_on_color_hex && !/^#[0-9a-f]{6}$/i.test(form.try_on_color_hex)" class="text-red-600">کد رنگ باید به شکل #RRGGBB باشد.</p>
            <div class="flex flex-wrap gap-3 mt-3">
              <label class="block">شدت اولیه
                <input v-model.number="form.try_on_opacity" type="number" min="0" max="1" step="0.01" dir="ltr" class="block border rounded px-3 py-2 w-28" />
              </label>
              <label class="block">پوشانندگی
                <input v-model.number="form.try_on_coverage" type="number" min="0" max="1" step="0.01" dir="ltr" class="block border rounded px-3 py-2 w-28" />
              </label>
              <label class="block">روشنایی رنگ
                <input v-model.number="form.try_on_brightness" type="number" min="0.5" max="1.5" step="0.01" dir="ltr" class="block border rounded px-3 py-2 w-28" />
              </label>
            </div>
            <p class="text-sm text-gray-500 mt-2">تنظیم هر تنوع مستقل است. برای کالیبراسیون، نتیجه را با نمونه واقعی در نور ثابت و روی لب روشن و تیره مقایسه کنید؛ نمایش همچنان تقریبی است.</p>
              </div>
          </section>
          <section v-if="form.product_type!='variable'"
                   class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button type="button" class="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-gray-50 border-b"
                    @click="togglePanel('details')">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-pink-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                </svg>
                <h2 class="text-sm font-semibold text-gray-800">جزئیات کالا</h2>
              </div>
              <Chevron :open="isOpen('details')"/>
            </button>

            <div v-show="isOpen('details')" class="p-4">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="space-y-1">
                  <Label>ستون قفسه</Label>
                  <input v-model="form.shelf_number" :disabled="user.user_type == 'prtnr'"
                         class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80"/>
                </div>
                <div class="space-y-1">
                  <Label>ردیف قفسه</Label>
                  <input v-model="form.aisle" :disabled="user.user_type == 'prtnr'"
                         class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80"/>
                </div>
                <div class="space-y-1">
                  <Label>تاریخ تولید</Label>
                  <date-field :disabled="user.user_type == 'prtnr'"
                              class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80"
                              v-model="form.product_date"/>
                </div>
                <div class="space-y-1">
                  <Label>تاریخ انقضا</Label>
                  <date-field :disabled="user.user_type == 'prtnr'"
                              class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80"
                              v-model="form.expired_date"/>
                </div>
              </div>
            </div>
          </section>
          <!-- اطلاعات پستی -->
          <section v-if="form.product_type!='variable'"
                   class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button type="button" class="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-gray-50 border-b"
                    @click="togglePanel('shipping')">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-blue-700" viewBox="0 0 24 24" fill="none" stroke="#00264d">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
                <h2 class="text-sm font-semibold text-gray-800">اطلاعات پستی</h2>
              </div>
              <Chevron :open="isOpen('shipping')"/>
            </button>

            <div v-show="isOpen('shipping')" class="p-4">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="space-y-1">
                  <cleave-money
                      :disabled="user.user_type == 'prtnr'"
                      :required="false"
                      label="وزن"
                      v-model="form.postal_weight"
                      currency-unit="گرم"
                  ></cleave-money>
                </div>

                <div class="space-y-1">
                  <cleave-money
                      :disabled="user.user_type == 'prtnr'"
                      :required="false"
                      label="طول"
                      v-model="form.length"
                      currency-unit="سانت"
                  ></cleave-money>
                </div>

                <div class="space-y-1">
                  <cleave-money
                      :disabled="user.user_type == 'prtnr'"
                      :required="false"
                      label="عرض"
                      v-model="form.width"
                      currency-unit="سانت"
                  ></cleave-money>
                </div>

                <div class="space-y-1">
                  <cleave-money
                      :disabled="user.user_type == 'prtnr'"
                      :required="false"
                      label="ارتفاع"
                      v-model="form.height"
                      currency-unit="سانت"
                  ></cleave-money>
                </div>
              </div>
            </div>
          </section>

          <!-- متغیرها -->
          <section v-if="form.product_type=='variable'"
                   class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button type="button" class="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-gray-50 border-b"
                    @click="togglePanel('variations')">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 5h16v2H4zM4 13h6v6H4zM16 13h4v6h-4z"/>
                </svg>
                <h2 class="text-sm font-semibold text-gray-800">تنوع محصول</h2>
              </div>
              <Chevron :open="isOpen('variations')"/>
            </button>

            <div v-show="isOpen('variations')" class="p-4 space-y-3">
              <button
                  v-if="!showVariationForm"
                  @click="showVariationForm = true"
                  type="button"
                  class="mb-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              >
                <svg class="h-4 w-4" viewBox="0 0 20 20" fill="white">
                  <path fill-rule="evenodd"
                        d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                        clip-rule="evenodd"/>
                </svg>
                <span class="text-gray-50">
                                  افزودن متغیر جدید
                </span>
              </button>

              <div class="rounded-lg border border-gray-200 overflow-hidden">
                <table class="min-w-full text-xs">
                  <thead class="bg-gray-50 text-gray-600">
                  <tr>
                    <th class="px-3 py-2 text-right">تصویر</th>
                    <th class="px-3 py-2 text-right">نام</th>
                    <th class="px-3 py-2 text-right">SKU</th>
                    <th class="px-3 py-2 text-right">موجودی</th>
                    <th class="px-3 py-2 text-right">قیمت</th>
                    <th class="px-3 py-2 text-right">عملیات</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(variation, index) in form.variations" :key="index"
                      class="border-t hover:bg-gray-50 cursor-pointer" @click="editVariation(variation)"
                      v-if="variation.active == true">
                    <td class="px-3 py-2">
                      <div v-if="variation.image" class="w-10 h-10 rounded border overflow-hidden">
                        <img :src="image_source(variation.image)" class="w-full h-full object-cover" alt="">
                      </div>
                    </td>
                    <td class="px-3 py-2 text-gray-700">{{ variation.name }}</td>
                    <td class="px-3 py-2 text-gray-700 font-mono">{{ variation.sixteen_digit_code }}</td>
                    <td class="px-3 py-2">
                        <span
                            class="px-2 py-0.5 rounded-full text-[11px] font-medium"
                            :class="{
                            'bg-green-100 text-green-800': variation.calculate_current_inventory > 10,
                            'bg-yellow-100 text-yellow-800': variation.calculate_current_inventory <= 10 && variation.calculate_current_inventory > 0,
                            'bg-red-100 text-red-800': variation.calculate_current_inventory <= 0
                          }"
                        >
                          {{
                            variation.calculate_current_inventory > 0 ? `${variation.calculate_current_inventory} در انبار` : 'ناموجود'
                          }}
                        </span>
                    </td>
                    <td class="px-3 py-2 text-gray-900 font-medium">{{ variation.price | toMoney }}</td>
                    <td class="px-3 py-2">
                      <div class="flex items-center gap-2">
                        <button class="text-indigo-600 hover:text-indigo-800" title="ویرایش"
                                @click="editVariation(variation)">✎
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!form.variations.length">
                    <td colspan="6" class="px-3 py-6 text-center text-gray-500">هنوز متغیری اضافه نشده است</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </main>

        <!-- Sidebar -->
        <aside class="lg:col-span-3 space-y-3">
          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-0 z-40">
            <div class="p-4 space-y-3">
              <div class="flex gap-2 pt-1">
                <button @click="goBack"
                        class="flex-1 px-3 py-2 rounded-xl text-center bg-red-400 hover:bg-red-800 text-white text-sm">
                  بازگشت
                </button>
                <button @click="submitForm" :disabled="submitting"
                        class="flex-1 px-3 py-2 rounded-xl bg-blue-600 text-center hover:bg-blue-700 text-white text-sm">
                  ذخیره
                </button>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-xl border border-gray-200">
            <div class="px-3 sm:px-4 py-3 rounded-t-xl bg-gray-50 border-b flex items-center justify-between">
              <h3 class="text-sm font-semibold text-gray-800">انتشار</h3>
            </div>
            <div class="p-4 space-y-3">
              <div class="space-y-1">
                <Label>نوع محصول</Label>
                <div class="select-wrap">
                  <select v-model="form.product_type" :disabled="user.user_type == 'prtnr'"
                          class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80 select-input text-xs">
                    <option value="simple">ساده (بدون متغیر)</option>
                    <option value="variable">دارای متغیر</option>
                  </select>
                </div>
              </div>
              <div class="space-y-1">
                <Label class="text-xs"> آخرین بروز رسانی : <span
                    class="text-[10px] text-blue-800">{{ form.new_at }}</span></Label>
                <date v-model="new_at" :class="{'ring-1 ring-rose-300': errors.new_at}"
                      :disabled="user.user_type == 'prtnr'"></date>
              </div>


              <div class="grid grid-cols-2 gap-3">
                <div class="space-y-1">
                  <Label>وضعیت</Label>
                  <div class="select-wrap">
                    <select v-model="form.status" :disabled="user.user_type == 'prtnr'"
                            class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80 select-input text-xs">
                      <option value="draft">پیش نویس</option>
                      <option value="pending">در انتظار تایید</option>
                      <option value="publish">منتشر شده</option>
                    </select>
                  </div>
                </div>
                <div class="space-y-1">
                  <Label>وضعیت محتوا</Label>
                  <div class="select-wrap">
                    <select v-model="form.content_status" :disabled="user.user_type == 'prtnr'"
                            class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80 select-input text-xs">
                      <option value="waiting">در انتظار تولید</option>
                      <option value="done_pending">در انتظار تأیید</option>
                      <option value="approved">تأیید شده</option>
                    </select>
                  </div>
                </div>
                <div class="col-span-2" v-if="form.product_type != 'variable' && id">
                  <div class="relative w-full inline-block text-left" v-cloak>
                    <button
                        @click="toggleLabelMenu(id)"
                        class="flex justify-between items-center w-full gap-1 px-2 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-800  to-blue-900  rounded-lg hover:from-gray-700 hover:to-blue-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd"
                              d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                              clip-rule="evenodd"/>
                      </svg>

                      <span>دریافت لیبل</span>

                      <span class="bg-white/20 rounded-md px-2 py-1 text-xs font-mono">
                      {{ form.sixteen_digit_code || 'بدون کد' }}
                    </span>

                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform duration-200"
                           :class="{'rotate-180': openLabelMenuId === id}" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clip-rule="evenodd"/>
                      </svg>
                    </button>

                    <div
                        v-if="openLabelMenuId === id"
                        class="origin-top-right absolute right-0 mt-2  rounded-md shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-20 overflow-hidden animate-fade-in w-full"
                    >
                      <div class="py-2 text-sm text-gray-700">
                        <button
                            @click="openLabel(id, 'html')"
                            class="w-full text-right px-4 py-3 hover:bg-blue-50 flex items-center gap-3 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" viewBox="0 0 20 20"
                               fill="currentColor">
                            <path fill-rule="evenodd"
                                  d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                                  clip-rule="evenodd"/>
                          </svg>
                          <span>پرینت بارکد</span>
                        </button>

                        <button
                            @click="openLabel(id, 'html_download')"
                            class="w-full text-right px-4 py-3 hover:bg-blue-50 flex items-center gap-3 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-500" viewBox="0 0 20 20"
                               fill="currentColor">
                            <path fill-rule="evenodd"
                                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                  clip-rule="evenodd"/>
                          </svg>
                          <span>دانلود بارکد </span>
                        </button>

                        <button
                            @click="openQr(id, 'html')"
                            class="w-full text-right px-4 py-3 hover:bg-blue-50 flex items-center gap-3 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-purple-500" viewBox="0 0 20 20"
                               fill="currentColor">
                            <path fill-rule="evenodd"
                                  d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1z"
                                  clip-rule="evenodd"/>
                            <path
                                d="M11 4a1 1 0 10-2 0v1a1 1 0 002 0V4zM10 7a1 1 0 011 1v1h2a1 1 0 110 2h-3a1 1 0 01-1-1V8a1 1 0 011-1zM16 9a1 1 0 100 2 1 1 0 000-2zM9 13a1 1 0 011-1h1a1 1 0 110 2v2a1 1 0 11-2 0v-3zM7 11a1 1 0 100-2H4a1 1 0 100 2h3zM17 13a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM16 17a1 1 0 100-2h-3a1 1 0 100 2h3z"/>
                          </svg>
                          <span>پرینت QRCode</span>
                        </button>

                        <button
                            @click="openQr(id, 'html_download')"
                            class="w-full text-right px-4 py-3 hover:bg-blue-50 flex items-center gap-3 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-orange-500" viewBox="0 0 20 20"
                               fill="currentColor">
                            <path fill-rule="evenodd"
                                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                  clip-rule="evenodd"/>
                          </svg>
                          <span>دانلود QRCode</span>
                        </button>
                      </div>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          </div>

          <!-- تصویر شاخص -->
          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div class="px-3 sm:px-4 py-3 bg-gray-50 border-b">
              <h3 class="text-sm font-semibold text-gray-800">تصویر محصول</h3>
            </div>
            <div class="grid grid-cols-1 grid-cols-1 p-2 items-center justify-center">

              <div>
                <smart-image-picker-modal
                    :disabled="user.user_type == 'prtnr'"
                    :accept="'image/*'"
                    :max-size-m-b="10"
                    :hint="'حداکثر ۱۰MB'"
                    :value="(form.image && typeof form.image === 'object') ? form.image : null"
                    :current-preview-url="(form.image && typeof form.image === 'string') ? image_source(form.image) : ''"
                    @input="onMainImageChange"
                />
              </div>
            </div>
          </div>

          <!-- گالری -->
          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div class="px-3 sm:px-4 py-3 bg-gray-50 border-b">
              <h3 class="text-sm font-semibold text-gray-800">گالری محصول</h3>
            </div>
            <div class="p-4 space-y-3">
              <div class="flex items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg text-gray-600">
                <label class="cursor-pointer text-blue-600 hover:text-blue-700">
                  <span>آپلود چند تصویر</span>
                  <input :disabled="user.user_type == 'prtnr'" type="file" multiple class="sr-only"
                         @change="handleGalleryUpload" accept="image/*">
                </label>
              </div>
              <div v-if="form.images && form.images.length" class="flex flex-wrap gap-2">
                <div v-for="(img, index) in form.images" :key="index" class="relative">
                  <img :src="image_source(img.preview)" class="h-16 w-16 object-cover rounded border">
                  <button
                      :disabled="user.user_type == 'prtnr'"
                      @click="removeGalleryImage(index)"
                      class="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full p-1"
                      title="حذف">✕
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div class="px-3 sm:px-4 py-3 bg-gray-50 border-b">
              <h3 class="text-sm font-semibold text-gray-800">دسته‌بندی و ویژگی‌ها</h3>
            </div>
            <div class="p-4 space-y-4">
              <div class="space-y-1">
                <Label>برند</Label>
                <brand-select :disabled="user.user_type == 'prtnr'" scope="local" v-model="form.brand"/>
                <FieldError v-if="errors.brand" :msg="errors.brand"/>
              </div>

              <div class="space-y-1" v-if="isManager">
                <Label>شریک</Label>
                <partner-select v-model="form.partner" clearable/>
                <FieldError v-if="errors.partner" :msg="errors.partner"/>
              </div>

              <category-tree-select
                  :disabled="user.user_type == 'prtnr'"
                  label="دسته‌بندی"
                  v-model="form.categorization"
                  :categories="categorizations"
                  api-url="product/categorization"
              />
              <category-tree-select
                  :disabled="user.user_type == 'prtnr'"
                  label="خواص"
                  v-model="form.avails"
                  :categories="avails"
                  api-url="product/avails"


              />
              <category-tree-select
                  :disabled="user.user_type == 'prtnr'"
                  label="ویژگی"
                  v-model="form.feature"
                  :categories="features"
                  api-url="product/feature"

              />
              <category-tree-select
                  :disabled="user.user_type == 'prtnr'"
                  label="طیف رنگی"
                  v-model="form.color_spectrum"
                  :categories="color_spectrum"
                  api-url="product/color_spectrum"
              />

            </div>
          </div>

          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div class="px-3 sm:px-4 py-3 bg-gray-50 border-b">
              <h3 class="text-sm font-semibold text-gray-800">اطلاعات سریع</h3>
            </div>
            <div class="p-4 grid grid-cols-2 gap-2 text-[12px] text-gray-700">
              <div class="bg-gray-50 rounded p-2">موجودی کل: <b class="font-bold">{{ total_inventory | toMoney }}</b>
              </div>
              <div v-if="form.product_type != 'variable'" class="bg-gray-50 rounded p-2">
                قیمت فروش: <b class="font-bold">{{ form.price | toMoney }}</b>
              </div>
              <div class="bg-gray-50 rounded  p-2">ارز: <b class="font-bold">{{ current_currency.name }}</b></div>
            </div>
          </div>
        </aside>
      </div>
    </div>

    <transition name="slide-fade">
      <div v-if="showNotification"
           class="fixed bottom-4 right-4 z-50 flex items-center p-4 w-full max-w-xs text-green-700 bg-green-50 rounded-lg shadow border border-green-200">
        <div class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-green-500 bg-green-100">✓</div>
        <div class="mr-3 text-sm font-medium pr-2">کالا با موفقیت ثبت شد</div>
        <button @click="showNotification=false" class="mr-auto text-gray-400 hover:text-gray-900 p-1.5">✕</button>
      </div>
    </transition>

    <div v-if="showVariationForm" class="fixed top-0 inset-0 z-50">
      <div class=" top-0 flex items-center justify-center " @click.self="cancelVariationEdit">
        <div class=" w-[100vw] overflow-y-auto">
          <admin-variation-from
              :parent-id="id"
              :variation-id="editingVariation? editingVariation.id : null "
              :currency="current_currency"
              :avails-options="avails"
              :features-options="features"
              @save="handleSaveVariation"
              @changed="handleVariationChanged"
              @done="handleVariationDone"
              @cancel="handleVariationCancel"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { appendTryOnCalibration, validTryOnCalibration, tryOnCalibrationFrom } from "./tryOnCalibration";

import CategorySelect from "@/components/selects/CategorySelect";
import CategoryTreeSelect from "@/modules/product_admin/CategoryTreeSelect";
import AdminVariationFrom from "@/modules/product_admin/AdminVariationForm";
import {quillEditor} from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import DateField from "@/components/mcomponents/cleave/DateField";
import StoreSelect from "@/components/selects/StoreSelect";
import BrandSelect from "@/components/selects/BrandSelect";
import PartnerSelect from "@/components/selects/PartnerSelect";
import CleaveMoney from "@/components/mcomponents/cleave/Money";
import SmartImagePickerModal from "@/components/scomponents/SmartImagePicker";

export default {
  name: 'NewAdminProductForm',
  components: {
    SmartImagePickerModal,
    CleaveMoney,
    BrandSelect, PartnerSelect, StoreSelect, AdminVariationFrom,
    CategoryTreeSelect, CategorySelect, quillEditor, DateField,
    Chevron: {
      functional: true,
      props: {open: Boolean},
      render(h, {props}) {
        return h('svg', {
          class: 'h-4 w-4 text-gray-500 transition-transform ' + (props.open ? 'rotate-180' : ''),
          attrs: {viewBox: '0 0 20 20', fill: 'currentColor'}
        }, [h('path', {
          attrs: {
            'fill-rule': 'evenodd',
            d: 'M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 011.08 1.04l-4.24 4.25a.75.75 0 01-1.08 0l-4.24-4.25a.75.75 0 01.02-1.06z',
            'clip-rule': 'evenodd'
          }
        })])
      }
    },
    SelectChevron: {
      functional: true,
      render(h) {
        return h('svg', {
              class: 'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500',
              attrs: {viewBox: '0 0 20 20', fill: 'currentColor'}
            },
            [h('path', {attrs: {d: 'M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z'}})]
        )
      }
    },
    Label: {
      functional: true,
      render(h, ctx) {
        const required = ctx.data && ctx.data.attrs && ctx.data.attrs.required;
        return h('label', {class: 'block text-[12px] font-medium text-gray-700 mb-1'}, [
          required ? h('span', {class: 'text-rose-500 ml-1'}, '*') : null,
          ...ctx.children
        ])
      }
    },
    FieldError: {
      functional: true,
      props: {msg: String},
      render(h, {props}) {
        return h('p', {class: 'mt-1 text-xs text-rose-600'}, props.msg)
      }
    }
  },
  props: {
    id: {},
    popUp: {default: false},
  },
  data() {
    return {
      generatedSku: '', // برای SKU تولید شده
      openLabelMenuId: null,
      currentContentTab: 'explanation',
      contentTabs: [
        {key: 'explanation', label: 'توضیحات'},
        {key: 'summary_explanation', label: 'توضیحات کوتاه'},
        {key: 'how_to_use', label: 'نحوه مصرف'},
        {key: 'faq', label: 'سوالات متداول'},
        {key: 'blog', label: 'بلاگ'},
      ],
      new_store_for_inventory: null,
      new_at: null,
      inventories: [],
      inventoriesLoading: false,
      openPanels: {
        content: true,
        pricing: true,
        inventory: true,
        details: true,
        variations: true,
        shipping: true
      },
      packing_inventory: {store: 1, name: 'پردازش', inventory: 0, minimum_inventory: 0},
      loading: false,
      submitting: false,
      errors: {},
      stores: [],
      editorOptions: {
        placeholder: 'متن خود را وارد کنید...',
        theme: 'snow',
        modules: {
          toolbar: [
            [{header: [1, 2, 3, 4, 5, 6, false]}],
            ['bold', 'italic', 'underline'],
            ['link', 'image'],
            [{list: 'ordered'}, {list: 'bullet'}],
            ['clean']
          ]
        }
      },
      form: {
        name: '',
        try_on_color_hex: '', ...tryOnCalibrationFrom(),
        sixteen_digit_code: '',
        explanation: '',
        faq: '',
        new_at: null,
        blog: '',
        schema: '',
        faq_schema: '',
        summary_explanation: '',
        how_to_use: '',
        regular_price: 0,
        price: 0,
        currency: '',
        inventory: 0,
        postal_weight: 0,
        shipping_cost: 0,
        length: 0,
        product_date: null,
        expired_date: null,
        width: 0,
        height: 0,
        status: 'draft',
        content_status: 'waiting',
        categorization: [],
        color_spectrum: [],
        feature: [],
        avails: [],
        brand: '',
        partner: '',
        product_type: 'simple',
        image: null,
        images: [],
        legend_pricing: 'true',
        base_price: 0,
        profit_type: 'percent',
        profit_margin: 0,
        discount_type: 'percent',
        discount_margin: 0,
        variations: [],
        aisle: '',
        shelf_number: '',
        inventories_in_stores: [],
        packing_inventory: {store: 1, name: 'پردازش', inventory: 0, minimum_inventory: 0},
      },
      currencies: [],
      avails: [],
      features: [],
      categorizations: [],
      color_spectrum: [],
      deletedGalleryImages: [],
      showNotification: false,
      brands: [],
      showVariationForm: false,
      editingVariation: null,
      editingIndex: -1
    }
  },
  filters: {
    toMoney(v) {
      const n = Number(v || 0);
      return n.toLocaleString('fa-IR');
    }
  },
  computed: {
    inventory_store_ids() {
      return (this.inventories || [])
          .map(r => r.store)
          .filter(Boolean);
    },
    has_new_inventory_row() {
      return (this.inventories || []).some(r => r._isNew);
    },
    total_inventory() {
      if (this.form.product_type !== 'variable') {
        return (this.inventories || []).reduce((s, r) => s + Number(r._inventoryDraft ?? r.inventory ?? 0), 0);
      }
      return (this.form.variations || []).reduce(
          (s, v) => s + Number(v?.calculate_current_inventory || 0),
          0
      );
    },
    current_currency() {
      if (this.form.currency) return this.currencies.find(c => c.id === this.form.currency) || {
        id: 0,
        name: 'تومان',
        exchange_rate_to_toman: 1
      }
      return {id: 0, name: 'تومان', exchange_rate_to_toman: 1}
    },
    base_price_with_rate() {
      return (this.current_currency.exchange_rate_to_toman || 1) * (this.form.base_price || 0)
    },
    shipping_cost_with_rate() {
      return (this.current_currency.exchange_rate_to_toman || 1) * (this.form.shipping_cost || 0)
    },
    final_amount() {
      let profitAmount = this.form.profit_type === 'percent'
          ? ((this.base_price_with_rate + this.shipping_cost_with_rate) / 100) * (this.form.profit_margin || 0)
          : (this.form.profit_margin || 0);
      let priceWithProfit = profitAmount + this.base_price_with_rate + this.shipping_cost_with_rate;
      let discountAmount = this.form.discount_type === 'percent'
          ? (priceWithProfit / 100) * (this.form.discount_margin || 0)
          : (this.form.discount_margin || 0);
      return priceWithProfit - discountAmount;
    },
    discount_sum() {
      let profitAmount = this.form.profit_type === 'percent'
          ? (this.base_price_with_rate / 100) * (this.form.profit_margin || 0)
          : (this.form.profit_margin || 0);
      let priceWithProfit = profitAmount + this.base_price_with_rate;
      return this.form.discount_type === 'percent'
          ? (priceWithProfit / 100) * (this.form.discount_margin || 0)
          : (this.form.discount_margin || 0);
    },
    profit_sum() {
      return this.form.profit_type === 'percent'
          ? ((this.base_price_with_rate + this.shipping_cost_with_rate) / 100) * (this.form.profit_margin || 0)
          : (this.form.profit_margin || 0);
    },
    user() {
      return this.$root?.user || {user_type: 'ma'}
    },
    isManager() {
      return !!(this.user && (this.user.user_type === 'ma' || this.user.is_superuser));
    }
  },
  watch: {
    id() {
      if (this.id) this.fetchProduct()
      else {
        this.form = {
          name: '',
        try_on_color_hex: '', ...tryOnCalibrationFrom(), sixteen_digit_code: '', explanation: '', aisle: '',
          faq: '', blog: '', schema: '', faq_schema: '', new_at: '',
          shelf_number: '', inventories_in_stores: [],
          packing_inventory: {store: 1, name: 'پردازش', inventory: 0, minimum_inventory: 0},
          summary_explanation: '', how_to_use: '',
          regular_price: 0, price: 0, currency: '', inventory: 0,
          postal_weight: 0, length: 0, product_date: null, expired_date: null, width: 0, height: 0,
          status: 'draft', avails: [], categorization: [], feature: [], brand: '', partner: '',
          product_type: 'simple', image: null, images: [],
          legend_pricing: 'true', base_price: 0, profit_type: 'percent', profit_margin: 0,
          discount_type: 'percent', discount_margin: 0, variations: []
        }
      }
    }
  },
  mounted() {
    this.new_at = this.getNowForDateFilter()
    this.fetchColorSpectrum();
    this.fetchCurrency();
    this.fetchCategories();
    this.fetchAvails();
    this.fetchFeatures();
    this.fetchBrands();
    this.getStores();
    if (this.id) this.fetchProduct(); else this.generateSKU();
  },
  methods: {
    onMainImageChange(payload) {
      // payload: null OR { file, preview }
      this.form.image = payload;     // همون shape ای که submitForm می‌خواد
    },
    generateSku() {
      const randomPart = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
      this.generatedSku = '9999' + randomPart;

      this.form.sixteen_digit_code = this.generatedSku;
      this.generatedSku = ''

    },
    toggleLabelMenu(productId) {
      this.openLabelMenuId = this.openLabelMenuId === productId ? null : productId;
    },
    openLabel(productId, mode) {
      const baseUrl = this.endpoint(`product/label/${productId}?token=${this.token}`);
      const url = `${baseUrl}&mode=${mode}`;

      if (mode === 'html') {
        window.open(url, '_blank');
      } else {
        window.open(url, '_blank');
      }

      this.openLabelMenuId = null;
    },
    openQr(productId, mode) {
      const baseUrl = this.endpoint(`product/qrCode/${productId}?token=${this.token}`);
      const url = `${baseUrl}&mode=${mode}`;

      if (mode === 'html') {
        window.open(url, '_blank');
      } else {
        window.open(url, '_blank');
      }

      this.openLabelMenuId = null;
    },

    isRowDirty(row) {
      if (row._isNew) return true;

      const currentVal = Number(row.inventory || 0);
      const draftVal = Number(row._inventoryDraft || 0);

      return currentVal !== draftVal;
    },

    handleChildSavedVariation(v) {
      const idx = this.form.variations.findIndex(x => x.id === v.id)
      if (idx >= 0) {
        this.form.variations.splice(idx, 1, v)
      } else {
        this.form.variations.push(v)
      }
      this.showVariationForm = false
      this.editingVariation = null
      this.editingIndex = -1
    },
    handleVariationCancel() {
      this.fetchAvails()
      this.fetchFeatures()
      if (this.id) {
        this.fetchProduct()
      }
      this.cancelVariationEdit()
    },
    handleVariationChanged() {
      if (this.id) {
        this.fetchProduct()
      }
    },
    handleVariationDone() {
      if (this.id) {
        this.fetchProduct()
      }
      this.cancelVariationEdit()
    },

    setContentTab(key) {
      this.currentContentTab = key
    },
    focusNextTab() {
      const i = this.contentTabs.findIndex(t => t.key === this.currentContentTab)
      const next = (i + 1) % this.contentTabs.length
      this.currentContentTab = this.contentTabs[next].key
    },
    focusPrevTab() {
      const i = this.contentTabs.findIndex(t => t.key === this.currentContentTab)
      const prev = (i - 1 + this.contentTabs.length) % this.contentTabs.length
      this.currentContentTab = this.contentTabs[prev].key
    },
    async loadInventories(productId) {
      if (!productId) return;
      this.inventoriesLoading = true;
      const tryUrl = this.endpoint(`product/v2/adminInventory?product=${productId}`);
      const fallbackUrl = this.endpoint(`product/v2/adminInventory`);

      const onSuccess = (rows) => {
        const list = (rows || []).filter(x => x.product === productId);
        this.inventories = list.map(x => ({
          ...x,
          _key: x.id || `${x.product}-${x.store}`,
          _isNew: false,
          _saving: false,
          _inventoryDraft: Number(x.inventory || 0),
        }));
        this.inventoriesLoading = false;
      };

      this.request({
        url: tryUrl,
        method: 'get',
        success: onSuccess,
        error: () => {
          this.request({
            url: fallbackUrl,
            method: 'get',
            success: onSuccess,
            finally: () => {
              this.inventoriesLoading = false;
            }
          });
        },
        finally: () => {
          this.inventoriesLoading = false;
        }
      });
    },

    addInventoryRow() {
      this.inventories.push({
        _key: `new-${Date.now()}`,
        _isNew: true,
        _saving: false,
        product: this.id,
        store: null,
        store_name: null,
        inventory: 0,
        _inventoryDraft: 0,
      });
    },

    removeNewRow(row) {
      const i = this.inventories.indexOf(row);
      if (i >= 0) this.inventories.splice(i, 1);
    },

    canSaveRow(row) {
      if (row._isNew) return !!this.new_store_for_inventory && row._inventoryDraft >= 0;
      return true;
    },
    saveInventoryRow(row) {
      console.log(row)
      if (!this.canSaveRow(row)) return;
      row._saving = true;

      if (row._isNew) {
        this.request({
          url: this.endpoint('product/v2/adminInventory'),
          method: 'post',
          data: {
            product: row.product,
            store: this.new_store_for_inventory,
            inventory: Number(row._inventoryDraft || 0),
          },
          success: (created) => {
            const i = this.inventories.indexOf(row);
            const normalized = {
              ...created,
              _key: created.id || `${created.product}-${created.store}`,
              _isNew: false,
              _saving: false,
              _inventoryDraft: Number(created.inventory || 0),
            };
            if (i >= 0) this.inventories.splice(i, 1, normalized);
            this.successNotify('موجودی انبار ایجاد شد')

            this.new_store_for_inventory = null
          },
          finally: () => {
            row._saving = false;
          }
        });
      } else if (row.id) {
        this.request({
          url: this.endpoint(`product/v2/adminInventory/${row.id}`),
          method: 'patch',
          data: {
            inventory: Number(row._inventoryDraft || 0),
            product: row.product,
            store: row.store,
          },
          success: (updated) => {
            row.inventory = Number(row._inventoryDraft || 0);
            this.successNotify('موجودی انبار به‌روزرسانی شد')
            row._saving = false
          },
          error: (e) => {
            row._inventoryDraft = Number(row.inventory || 0);
            this.deleteNotify('خطا در به‌روزرسانی', 'خطا در به‌روزرسانی موجودی')
          },
          finally: () => {
            row._saving = false;
          }
        });
      } else {
        row._saving = false;
      }
    },

    isOpen(key) {
      return !!this.openPanels[key]
    },
    togglePanel(key) {
      this.$set(this.openPanels, key, !this.openPanels[key])
    },

    asString(v) {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      return ''
    },
    pad2(n) {
      n = parseInt(n, 10);
      if (isNaN(n)) return "00";
      return n < 10 ? "0" + n : "" + n;
    },

    getNowForDateFilter() {
      const now = new Date();

      const gy = now.getFullYear();
      const gm = now.getMonth() + 1;
      const gd = now.getDate();

      const hh = this.pad2(now.getHours());
      const mi = this.pad2(now.getMinutes());
      const ss = this.pad2(now.getSeconds());

      const Y = gy;
      const M = this.pad2(gm);
      const D = this.pad2(gd);

      return `${Y}-${M}-${D} ${hh}:${mi}:${ss}`;
    },
    getStores() {
      this.request({
        url: this.endpoint(`main/store`),
        method: "get",
        success: data => {
          this.stores = []
          for (let store of data) if (store.id != 1) this.stores.push(store.id)
        }
      })
    },
    removeStore(index) {
      this.form.inventories_in_stores.splice(index, 1)
    },
    removeMainImage() {
      this.form.image = null
    },
    reset() {
      this.id = null;
      this.$router.push('/panel/adminProductForm')
    },
    removeGalleryImage(index) {
      if (this.form.images[index].id) this.deletedGalleryImages.push(this.form.images[index].id);
      this.form.images.splice(index, 1);
    },
    fetchProduct() {
      this.loading = true
      this.request({
        url: this.endpoint(`product/adminProductGet/${this.id}`),
        method: 'get',
        success: (response) => {
          this.form = {
            name: response.name || '',
            try_on_color_hex: response.try_on_color_hex || '', ...tryOnCalibrationFrom(response),
            sixteen_digit_code: response.sixteen_digit_code || '',
            explanation: this.asString(response.explanation),
            summary_explanation: this.asString(response.summary_explanation),
            how_to_use: this.asString(response.how_to_use),
            faq: this.asString(response.faq),
            blog: this.asString(response.blog),
            schema: this.asString(response.schema),
            faq_schema: this.asString(response.faq_schema),
            expired_date: response.expired_date || null,
            product_date: response.product_date || null,
            regular_price: response.regular_price || 0,
            price: response.price || 0,
            currency: response.currency || '',
            inventory: response.calculate_current_inventory || 0,
            postal_weight: response.postal_weight || 0,
            length: response.length || 0,
            width: response.width || 0,
            shipping_cost: response.shipping_cost || 0,
            height: response.height || 0,
            status: response.status || 'draft',
            content_status: response.content_status || 'waiting',
            categorization: response.categorization || '',
            color_spectrum: response.color_spectrum || '',
            avails: response.avails || '',
            feature: response.feature || '',
            new_at: response.new_at || '',
            aisle: response.aisle || '',
            shelf_number: response.shelf_number || '',
            inventories_in_stores: response.inventories_in_stores || [],
            packing_inventory: response.packing_inventory,
            brand: response.brand || '',
            partner: response.partner || '',
            product_type: response.product_type || 'simple',
            image: response.picture || null,
            images: [],
            variations: (response.variations || []).map(v => ({
              ...v,
              legend_pricing: 'true'
            })),
            legend_pricing: 'true',
            base_price: response.base_price,
            profit_type: response.profit_type,
            profit_margin: response.profit_margin,
            discount_type: response.discount_type,
            discount_margin: response.discount_margin,
          };
          for (let item of (response.gallery || [])) {
            this.form.images.push({id: item.id, file: null, preview: item.picture})
          }
          this.loadInventories(response.id || this.id);

          this.loading = false
        },
        finally: () => {
          this.loading = false
        }
      })
    },
    fetchCategories() {
      this.request({
        url: this.endpoint('product/categorizationTree'), method: 'get',
        success: (data) => {
          this.categorizations = data
        }
      })
    },
    fetchColorSpectrum() {
      this.request({
        url: this.endpoint('product/colorSpectrumTree'), method: 'get',
        success: (data) => {
          this.color_spectrum = data
        }
      })
    },
    fetchAvails() {
      this.request({
        url: this.endpoint('product/availsTree'), method: 'get',
        success: (data) => {
          this.avails = data
        }
      })
    },
    fetchFeatures() {
      this.request({
        url: this.endpoint('product/featureTree'), method: 'get',
        success: (data) => {
          this.features = data
        }
      })
    },
    fetchBrands() {
      this.request({
        url: this.endpoint('api/brands'), method: 'get',
        success: (data) => {
          this.brands = data
        }
      })
    },
    fetchCurrency() {
      this.request({
        url: this.endpoint('main/currency'), method: 'get',
        success: (data) => {
          this.currencies = data
        }
      })
    },
    generateSKU() {
      this.form.sixteen_digit_code = ''
    },
    addStoreInventory() {
      this.form.inventories_in_stores.push({name: 'new', inventory: 0, store: null})
    },
    handleImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.form.image = {file, preview: ev.target.result}
      };
      reader.readAsDataURL(file);
    },
    showNotificationMessage() {
      this.showNotification = true;
      setTimeout(() => this.showNotification = false, 4000)
    },
    handleGalleryUpload(e) {
      const files = e.target.files;
      if (!files || !files.length) return;
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.form.images.push({id: null, file, preview: ev.target.result})
        };
        reader.readAsDataURL(file);
      });
    },
    submitForm() {
      if (!validTryOnCalibration(this.form)) {
        window.alert('شدت و پوشانندگی باید بین ۰ و ۱، و روشنایی بین ۰٫۵ و ۱٫۵ باشند.');
        return;
      }
      if (this.form.try_on_color_hex && !/^#[0-9a-f]{6}$/i.test(this.form.try_on_color_hex)) {
        window.alert('کد رنگ باید به شکل #RRGGBB باشد.');
        return;
      }

      this.submitting = true;
      this.errors = {};
      try {
        const fd = new FormData();
        const categorization_ids = (this.form.categorization || []).map(cat => cat.id);
        const color_ids = (this.form.color_spectrum || []).map(cat => cat.id);
        const features_ids = (this.form.feature || []).map(cat => cat.id);
        const avails_ids = (this.form.avails || []).map(cat => cat.id);

        fd.append('try_on_color_hex', this.form.try_on_color_hex || '');

        appendTryOnCalibration(fd, this.form);
      fd.append('name', this.form.name);
        fd.append('sixteen_digit_code', this.form.sixteen_digit_code);
        fd.append('explanation', this.form.explanation || '');
        fd.append('faq', this.form.faq || '');
        fd.append('blog', this.form.blog || '');
        fd.append('schema', this.form.schema || null);
        fd.append('faq_schema', this.form.faq_schema || null);
        fd.append('summary_explanation', this.form.summary_explanation || '');
        fd.append('how_to_use', this.form.how_to_use || '');
        fd.append('new_at', this.new_at || '');
        fd.append('regular_price', this.form.regular_price);
        fd.append('price', this.form.price);
        fd.append('currency', this.form.currency);
        fd.append('product_date', `${this.form.product_date || ''}`);
        fd.append('expired_date', `${this.form.expired_date || ''}`);
        fd.append('calculate_current_inventory', this.form.calculate_current_inventory || 0);
        fd.append('postal_weight', this.form.postal_weight || 0);
        fd.append('length', this.form.length || 0);
        fd.append('width', this.form.width || 0);
        fd.append('shipping_cost', this.form.shipping_cost || 0);
        fd.append('height', this.form.height || 0);
        fd.append('status', this.form.status);
        fd.append('content_status', this.form.content_status);
        fd.append('product_type', this.form.product_type);
        fd.append('aisle', this.form.aisle);
        fd.append('shelf_number', this.form.shelf_number);
        fd.append('legend_pricing', 'true');
        fd.append('base_price', this.form.base_price || 0);
        fd.append('profit_type', this.form.profit_type || 'percent');
        fd.append('profit_margin', this.form.profit_margin || 0);
        fd.append('discount_type', this.form.discount_type || 'percent');
        fd.append('discount_margin', this.form.discount_margin || 0);

        categorization_ids.forEach(pk => fd.append('categorization_ids', pk));
        color_ids.forEach(pk => fd.append('colors_ids', pk));
        avails_ids.forEach(pk => fd.append('avails_ids', pk));
        features_ids.forEach(pk => fd.append('features_ids', pk));

        if (this.form.brand) fd.append('brand', this.form.brand);
        if (this.form.partner) fd.append('partner', this.form.partner);

        if (!this.form.image && this.id) {
          fd.append('remove_image', 'true');
        } else if (this.form.image) {
          if (this.form.image.file) {
            fd.append('image', this.form.image.file);
            fd.append('remove_image', 'false');
          } else if (typeof this.form.image === 'string') {
            fd.append('remove_image', 'false');
          }
        }

        if (this.form.images && this.form.images.length > 0) {
          this.form.images.forEach(img => {
            if (img.file) fd.append('images', img.file)
          })
        }

        if (this.deletedGalleryImages.length > 0) {
          this.deletedGalleryImages.forEach(pk => fd.append('deleted_gallery_images', pk))
        }

        const url = this.id ? this.endpoint(`product/adminProductUpdate/${this.id}`) : this.endpoint('product/adminProductCreate');
        const method = this.id ? 'put' : 'post';

        this.request({
          url, method, data: fd, headers: {'Content-Type': 'multipart/form-data'},
          success: (data) => {
            this.successNotify('کالا با موفقیت ثبت شد')
            this.showNotificationMessage();
            this.id = data.id;
            this.fetchProduct();
            this.submitting = false
          }
        });
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors = error.response.data.errors || {};
          this.deleteNotify('خطا در به‌روزرسانی', 'لطفاً مقادیر را بررسی کنید')
        } else {
          this.deleteNotify('خطا در به‌روزرسانی', 'خطا در ذخیره اطلاعات')
          console.error('submit error', error);
        }
      } finally {
        this.submitting = false;
      }
    },
    image_source(image) {
      if (!image) return 'https://via.placeholder.com/150';
      if (/^\/media/.test(image)) return this.img_source(image)
      if (typeof image === 'string') return image;
      if (image.preview) return image.preview;
      if (image.file) return URL.createObjectURL(image.file);
      return 'https://via.placeholder.com/150';
    },
    goBack() {
      if (this.user.user_type == 'prtnr') {
        this.$router.push({name: 'PartnerProductList', query: {...this.$route.query}})

      } else {
        this.$router.push({name: 'AdminProductList', query: {...this.$route.query}})
      }
    },
    handleSaveVariation(variationData) {
      if (this.editingIndex >= 0) this.form.variations.splice(this.editingIndex, 1, variationData)
      else this.form.variations.push(variationData)
      this.cancelVariationEdit()
    },
    editVariation(variation) {
      this.editingVariation = variation
      this.editingIndex = this.form.variations.indexOf(variation)
      this.showVariationForm = true
    },
    removeVariation(index) {
      const i = typeof index === 'number' ? index : this.form.variations.indexOf(index)
      if (i >= 0) this.form.variations.splice(i, 1)
    },
    cancelVariationEdit() {
      this.showVariationForm = false
      this.editingVariation = null
      this.editingIndex = -1
    }
  }
}
</script>

<style scoped>
/* Dark Theme */
.bg-gray-50\/80 {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(10px);
}

.text-gray-800 {
  color: rgba(226, 232, 240, 1) !important; /* slate-200 */
}

.text-gray-700 {
  color: rgba(148, 163, 184, 1) !important; /* slate-400 */
}

.text-gray-600 {
  color: rgba(148, 163, 184, 0.8) !important;
}

/* Main content background */
.bg-white {
  background: rgba(15, 23, 42, 0.7) !important;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* Headers and panel titles */
.bg-gray-50 {
  background: rgba(30, 41, 59, 0.7) !important; /* slate-800 with transparency */
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Borders */
.border-gray-200,
.border-gray-300 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Inputs and selects */
input, select, textarea {
  border-radius: 10px;
  padding: 7px;
  background: rgba(30, 41, 59, 0.6) !important;
  color: rgba(226, 232, 240, 1) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.5) !important; /* indigo-500 */
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.25) !important;
}

/* Blue tint backgrounds */
.bg-blue-50\/80 {
  background: rgba(30, 58, 138, 0.3) !important; /* blue-900 with transparency */
  color: rgba(226, 232, 240, 1) !important;
}

/* Placeholder text */
::placeholder {
  color: rgba(148, 163, 184, 0.6) !important;
}

/* Table styles */
.bg-gray-50.text-gray-600 {
  background: rgba(30, 41, 59, 0.8) !important;
  color: rgba(148, 163, 184, 1) !important;
}

.border-gray-200 {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Hover effects */
.hover\:bg-gray-50:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

/* Button styles */
.bg-blue-600 {
  background: linear-gradient(135deg, #6366f1, #8b5cf6) !important; /* indigo-500 to violet-500 */
}

.bg-red-400 {
  background: linear-gradient(135deg, #f87171, #ef4444) !important; /* red-400 to red-500 */
}

.bg-blue-600:hover {
  background: linear-gradient(135deg, #4f46e5, #7c3aed) !important;
}

.bg-red-400:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c) !important;
}

/* Status badges */
.bg-green-100 {
  background: rgba(6, 78, 59, 0.3) !important; /* emerald-900 */
}

.text-green-800 {
  color: rgba(110, 231, 183, 1) !important; /* emerald-300 */
}

.bg-yellow-100 {
  background: rgba(113, 63, 18, 0.3) !important; /* amber-900 */
}

.text-yellow-800 {
  color: rgba(253, 230, 138, 1) !important; /* amber-200 */
}

.bg-red-100 {
  background: rgba(127, 29, 29, 0.3) !important; /* red-900 */
}

.text-red-800 {
  color: rgba(252, 165, 165, 1) !important; /* red-300 */
}

/* Gradient text */
.bg-clip-text {
  background-image: linear-gradient(90deg, #a855f7, #ec4899, #3b82f6) !important;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  background: rgba(15, 23, 42, 0.5);
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #6366f1, #8b5cf6);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #4f46e5, #7c3aed);
}

/* Quill Editor dark theme */
.ql-toolbar.ql-snow {
  background: rgba(30, 41, 59, 0.8) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.ql-container.ql-snow {
  background: rgba(30, 41, 59, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(226, 232, 240, 1) !important;
}

.ql-editor {
  color: rgba(226, 232, 240, 1) !important;
}

.ql-snow .ql-stroke {
  stroke: rgba(226, 232, 240, 0.8) !important;
}

.ql-snow .ql-fill {
  fill: rgba(226, 232, 240, 0.8) !important;
}

.ql-snow .ql-picker {
  color: rgba(226, 232, 240, 0.8) !important;
}

/* File upload areas */
.border-dashed.border-gray-300 {
  border-color: rgba(255, 255, 255, 0.2) !important;
  background: rgba(30, 41, 59, 0.3) !important;
}

.text-gray-500 {
  color: rgba(148, 163, 184, 0.8) !important;
}

/* Notification */
.bg-green-50 {
  background: rgba(6, 78, 59, 0.3) !important;
  border-color: rgba(16, 185, 129, 0.3) !important;
}

.text-green-700 {
  color: rgba(110, 231, 183, 1) !important;
}

.bg-green-100 {
  background: rgba(6, 78, 59, 0.5) !important;
}

/* Label dropdown menu */
.bg-gradient-to-r.from-blue-800.to-blue-900 {
  background: linear-gradient(90deg, #1e40af, #1e3a8a) !important;
}

.ring-1.ring-rose-300 {
  --tw-ring-color: rgba(244, 63, 94, 0.5) !important; /* rose-500 */
}

/* Dropdown menu */
.bg-white.ring-1.ring-black {
  background: rgba(15, 23, 42, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.hover\:bg-blue-50:hover {
  background: rgba(30, 64, 175, 0.2) !important;
}

/* Inventory chip */
.bg-gradient-to-r.from-indigo-600.to-blue-600 {
  background: linear-gradient(90deg, #4f46e5, #2563eb) !important;
}

/* Variation button */
.bg-gradient-to-r.from-indigo-500.to-purple-600 {
  background: linear-gradient(90deg, #6366f1, #7c3aed) !important;
}

.bg-gradient-to-r.from-indigo-500.to-purple-600:hover {
  background: linear-gradient(90deg, #4f46e5, #6d28d9) !important;
}

/* Quick info cards */
.bg-gray-50 {
  background: rgba(30, 41, 59, 0.5) !important;
}

/* Form input focus */
.focus\:ring-2.focus\:ring-blue-500:focus {
  --tw-ring-color: rgba(99, 102, 241, 0.5) !important;
}

/* Image upload hover */
.hover\:border-blue-400:hover {
  border-color: rgba(96, 165, 250, 0.6) !important; /* blue-400 */
}

/* Disabled inputs */
input:disabled, select:disabled, textarea:disabled {
  background: rgba(30, 41, 59, 0.4) !important;
  color: rgba(148, 163, 184, 0.5) !important;
  cursor: not-allowed;
}

/* Animation for transitions */
.transition-colors {
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}

/* Selection color */
::selection {
  background: rgba(99, 102, 241, 0.3) !important;
  color: rgba(226, 232, 240, 1) !important;
}

/* Date field styles */
.date-field {
  background: rgba(30, 41, 59, 0.6) !important;
  color: rgba(226, 232, 240, 1) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.date-field:focus {
  border-color: rgba(99, 102, 241, 0.5) !important;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.25) !important;
}

/* Store select */
.store-select {
  background: rgba(30, 41, 59, 0.6) !important;
  color: rgba(226, 232, 240, 1) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

/* Searchable selects */
.searchable-select {
  background: rgba(30, 41, 59, 0.8) !important;
  color: rgba(226, 232, 240, 1) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}


/* Cleave money component */
.cleave-money {
  background: rgba(30, 41, 59, 0.6) !important;
  color: rgba(226, 232, 240, 1) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

/* Tab navigation */
.bg-blue-600.text-white {
  background: linear-gradient(135deg, #4f46e5, #7c3aed) !important;
}

.bg-gray-200 {
  background: rgba(255, 255, 255, 0.05) !important;
}

.hover\:bg-gray-700:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.text-gray-600 {
  color: rgba(148, 163, 184, 0.8) !important;
}

.hover\:text-white:hover {
  color: rgba(226, 232, 240, 1) !important;
}

/* Glass morphism effect */
.backdrop-blur {
  backdrop-filter: blur(12px);
}

/* Loading spinner */
.animate-spin {
  color: #6366f1 !important; /* indigo-500 */
}

/* Error messages */
.text-rose-600 {
  color: #fda4af !important; /* rose-300 */
}

.border-red-300 {
  border-color: rgba(253, 164, 175, 0.5) !important;
}

/* Success messages */
.text-green-500 {
  color: #34d399 !important; /* emerald-400 */
}

/* Warning messages */
.text-yellow-500 {
  color: #fbbf24 !important; /* amber-400 */
}

/* Icons */
.text-blue-500 {
  color: #60a5fa !important; /* blue-400 */
}

.text-yellow-500 {
  color: #fbbf24 !important; /* amber-400 */
}

.text-pink-600 {
  color: #ec4899 !important; /* pink-500 */
}

.text-blue-700 {
  color: #3b82f6 !important; /* blue-500 */
}

.text-indigo-600 {
  color: #6366f1 !important; /* indigo-500 */
}

/* Background overlays */
.bg-white\/90 {
  background: rgba(15, 23, 42, 0.9) !important;
}

/* Remove button */
.bg-rose-600 {
  background: linear-gradient(135deg, #f87171, #ef4444) !important;
}

/* Toggle button */
.border-gray-700 {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.bg-gray-600 {
  background: rgba(71, 85, 105, 0.6) !important; /* slate-600 */
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .bg-white {
    backdrop-filter: blur(8px);
  }

  input, select, textarea {
    font-size: 16px !important;
  }
}

/* Print styles for labels */
@media print {
  .bg-white {
    background: white !important;
    color: black !important;
  }
}
</style>